import * as React from 'react';

import ActivityRow from './activity_row';
import { Activity, Topic } from './interfaces';
import Pagination from './pagination';
import { ADDITIONAL_ACTIVITIES_THRESHOLD, lowerBound, sortFunctions, upperBound, } from './shared';
import SortDropdown from './sort_dropdown';

import { requestPost, } from '../../../../../../modules/request';
import { EVIDENCE, LESSONS } from '../../../../../Shared';
import ActivityDisclaimerBanner from '../../../shared/activityDisclaimerBanner';

const searchIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/search.svg`
const closeIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/close.svg`
const filterIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/icons-filter.svg`
const sortIconSrc = `${window.postbuildEnv.CDN_URL}/images/icons/icons-sort.svg`
const emptySearchSrc = `${window.postbuildEnv.CDN_URL}/images/illustrations/search-empty.svg`
const lightbulbSrc = `${window.postbuildEnv.CDN_URL}/images/icons/s/lightbulb.svg`

interface AdditionalActivityGroup {
  band: string,
  activities: Activity[]
}

const gradeBandLegend = (
  <div className="grade-band-legend">
    <span className="legend-item"><span className="legend-swatch target-grade-range" />Target grade range</span>
    <span className="legend-item"><span className="legend-swatch higher-grade" />Also suitable for higher grades</span>
  </div>
)

interface ActivityTableContainerProps {
  filteredActivities: Activity[],
  selectedActivities: Activity[],
  toggleActivitySelection: (activity: Activity) => void,
  currentPage: number,
  setCurrentPage: (currentPage: number) => void,
  search: string,
  handleSearch: (event: any) => void,
  undoLastFilter: (event: any) => void,
  resetAllFilters: (event: any) => void,
  setShowMobileFilterMenu: (showMenu: boolean) => void,
  setShowMobileSortMenu: (showMenu: boolean) => void,
  sort: string,
  setSort: (sort: string) => void,
  saveActivity: (activityId: number) => void,
  unsaveActivity: (activityId: number) => void,
  savedActivityIds: number[],
  showEvidenceBanner: boolean,
  showLessonsBanner: boolean,
  additionalActivityGroups: AdditionalActivityGroup[],
  topics: Topic[]
}

const FilterAndSort = ({ setShowMobileFilterMenu, setShowMobileSortMenu, }) => {
  function openMobileFilterMenu() { setShowMobileFilterMenu(true) }
  function openMobileSortMenu() { setShowMobileSortMenu(true) }
  return (
    <section className="filter-and-sort">
      <button aria-label="Open filter menu" className="interactive-wrapper focus-on-light" onClick={openMobileFilterMenu} type="button">
        <img alt="" src={filterIconSrc} />
        Filters
      </button>
      <span className="divider" />
      <button aria-label="Open sort menu" className="interactive-wrapper focus-on-light" onClick={openMobileSortMenu} type="button">
        <img alt="" src={sortIconSrc} />
        Sort by
      </button>
    </section>
  )
}

const SearchAndSort = ({ handleSearch, search, setSort, showGradeRangeLegend, sort, }) => {
  function handleClickClear() { handleSearch('') }
  function handleInputChange(e) { handleSearch(e.target.value) }

  const clearButton = !!search.length && <button className="interactive-wrapper close-icon focus-on-light" onClick={handleClickClear} type="button"><img alt="Clear icon" src={closeIconSrc} /></button>
  return (
    <React.Fragment>
      <section className="search-and-sort">
        <div className="search-wrapper">
          <img alt="" className="search-icon" src={searchIconSrc} />
          <input
            aria-label="Search for concepts, activities, and more"
            onChange={handleInputChange}
            placeholder="Search for concepts, activities, and more"
            value={search}
          />
          {clearButton}
        </div>
        <SortDropdown setSort={setSort} sort={sort} />
      </section>
      {showGradeRangeLegend && gradeBandLegend}
    </React.Fragment>
  )
}

const EmptyState = ({ undoLastFilter, resetAllFilters, }) => {
  return (
    <div className="empty-state">
      <img alt="Illustration of a magnifying glass over an empty document" src={emptySearchSrc} />
      <h3>No results</h3>
      <p>Undo your last filter to see activities. Or clear all filters.</p>
      <div className="empty-state-button-wrapper">
        <button className="focus-on-light quill-button medium outlined grey" onClick={resetAllFilters} type="button">Clear all filters</button>
        <button className="focus-on-light quill-button medium contained green" onClick={undoLastFilter} type="button">Undo</button>
      </div>
    </div>
  )
}

const Banner = ({ bannerShowing, selectedActivities, closeBanner, type }) => {
  if (!bannerShowing) { return <span /> }
  if (!selectedActivities.some(selectedActivity => selectedActivity.activity_classification.key === type)) { return <span /> }

  return <ActivityDisclaimerBanner activityType={type} closeBanner={closeBanner} />
}

const ActivityTableContainer = ({
  filteredActivities,
  selectedActivities,
  toggleActivitySelection,
  currentPage,
  setCurrentPage,
  search,
  handleSearch,
  undoLastFilter,
  resetAllFilters,
  setShowMobileFilterMenu,
  setShowMobileSortMenu,
  sort,
  setSort,
  saveActivity,
  unsaveActivity,
  savedActivityIds,
  showEvidenceBanner,
  showLessonsBanner,
  additionalActivityGroups,
  topics,
}: ActivityTableContainerProps) => {
  const [lessonsBannerShowing, setLessonsBannerShowing] = React.useState(showLessonsBanner)
  const [evidenceBannerShowing, setEvidenceBannerShowing] = React.useState(showEvidenceBanner)
  const sortedActivities = sort ? sortFunctions[sort]([...filteredActivities]) : filteredActivities

  const additionalActivities = additionalActivityGroups.flatMap(group => group.activities)
  const additionalBands = additionalActivityGroups.map(group => group.band)
  const showAdditionalActivities = filteredActivities.length < ADDITIONAL_ACTIVITIES_THRESHOLD && additionalActivities.length > 0
  const combinedActivities = showAdditionalActivities ? sortedActivities.concat(additionalActivities) : sortedActivities

  const onBandCount = sortedActivities.length
  const pageStart = lowerBound(currentPage)
  const currentPageActivities = combinedActivities.slice(pageStart, upperBound(currentPage));

  function closeLessonsBanner() {
    setLessonsBannerShowing(false)
    requestPost('/milestones/complete_acknowledge_lessons_banner')
  }

  function closeEvidenceBanner() {
    setEvidenceBannerShowing(false)
    requestPost('/milestones/complete_acknowledge_evidence_banner')
  }

  function goToPage(page) {
    setCurrentPage(page)
    const scrollContainer = document.getElementsByClassName('main-content-container')[0]
    scrollContainer && scrollContainer.scrollTo(0, 0)
  }


  function buildActivityRow(act, i) {
    const isSelected = selectedActivities.some(s => s.id === act.id)
    return (
      <ActivityRow
        activity={act}
        allTopics={topics}
        isFirst={i === 0}
        isSelected={isSelected}
        key={act.id}
        saveActivity={saveActivity}
        savedActivityIds={savedActivityIds}
        toggleActivitySelection={toggleActivitySelection}
        unsaveActivity={unsaveActivity}
      />
    )
  }

  function buildAdditionalCallout(continued) {
    return (
      <div className="additional-activities-callout" key={continued ? 'additional-callout-continued' : 'additional-callout'}>
        <img alt="" src={lightbulbSrc} />
        <div className="additional-activities-callout-text">
          <h3>Additional Activities{continued && <span className="continued"> (continued)</span>}</h3>
          {!continued && <p>{additionalBands.join(' and ')} target grade activities can still be valuable for students in higher grades</p>}
        </div>
      </div>
    )
  }

  const pageContent = []
  if (showAdditionalActivities && onBandCount < pageStart) {
    pageContent.push(buildAdditionalCallout(true))
  }
  currentPageActivities.forEach((act, i) => {
    if (showAdditionalActivities && pageStart + i === onBandCount) {
      pageContent.push(buildAdditionalCallout(false))
    }
    pageContent.push(buildActivityRow(act, i))
  })

  const activityRowsOrEmptyState = combinedActivities.length
    ? <section aria-label="Activity list">{pageContent}</section>
    : <EmptyState resetAllFilters={resetAllFilters} undoLastFilter={undoLastFilter} />

  return (
    <section className="activity-table-container">
      <div className="banners-container">
        <Banner bannerShowing={lessonsBannerShowing} closeBanner={closeLessonsBanner} selectedActivities={selectedActivities} type={LESSONS} />
        <Banner bannerShowing={evidenceBannerShowing} closeBanner={closeEvidenceBanner} selectedActivities={selectedActivities} type={EVIDENCE} />
      </div>
      <SearchAndSort handleSearch={handleSearch} search={search} setSort={setSort} showGradeRangeLegend={!!currentPageActivities.length} sort={sort} />
      <FilterAndSort setShowMobileFilterMenu={setShowMobileFilterMenu} setShowMobileSortMenu={setShowMobileSortMenu} />
      {activityRowsOrEmptyState}
      <Pagination activities={combinedActivities} currentPage={currentPage} setCurrentPage={goToPage} />
    </section>
  )
}

export default ActivityTableContainer
